'use client';

import React, { useMemo } from 'react';

import Image from 'next/image';
import { z } from 'zod';

import AlertCircle from '@/assets/images/alert-circle.svg';
import LeftArrow from '@/assets/images/arrow-left.svg';
import Button from '@/components/V2/Button';
import ButtonLink from '@/components/V2/ButtonLink';
import Spinner from '@/components/V2/PetProfile/Spinner';
import TextInput from '@/components/V2/TextInput';
import { getClientEnv } from '@/config/clientEnv';
import { useForgotPasswordContext } from '@/contexts/ForgotPasswordContext';
import useForm from '@/helpers/hooks/useForm';

const forgotPasswordSchema = z.object({
  email: z.string().min(1, 'Email Address is required').email('Please enter a valid email address.'),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;
type ForgotPasswordFormErrors = Partial<Record<keyof ForgotPasswordFormData, string>>;

const ForgotPasswordForm = () => {
  const { customerEmail, handleForgotPasswordSubmit, handleBackToLogin, error, isLoading } = useForgotPasswordContext();
  const initialState = useMemo(
    () => ({
      email: customerEmail,
    }),
    []
  );

  const {
    data,
    errors,
    handleChange,
    onSubmit: handleFormSubmit,
  } = useForm<ForgotPasswordFormData, ForgotPasswordFormErrors>({
    initialState,
    errors: {} as ForgotPasswordFormErrors,
    validationSchema: forgotPasswordSchema,
  });

  const handleSubmit = () => {
    handleFormSubmit({ preventDefault: () => {} } as React.FormEvent<HTMLFormElement>, () => {
      handleForgotPasswordSubmit(data.email);
    });
  };

  const RSLOGINURL = `${getClientEnv().REPLICATED_URL}/pawtree/account/login`;

  return (
    <div className="">
      <div className="v2-flex v2-items-center v2-mb-8">
        <ButtonLink
          scheme={'primary'}
          variant={'outlined'}
          className="!v2-border-0 !v2-p-0 !v2-rounded-0 v2-font-sweet-sans-pro-bold v2-font-bold !v2-text-sm v2-w-fit v2-flex v2-items-center !v2-leading-[28px] v2-gap-3"
          onClick={() => handleBackToLogin()}
        >
          <Image src={LeftArrow} alt="" />
          Back
        </ButtonLink>
      </div>
      <h2 className="v2-font-sweet-sans-pro-bold v2-font-bold v2-text-3xl v2-text-[#101828] v2-mb-3">
        Forgot password?
      </h2>

      <p className="v2-font-sweet-sans-pro v2-font-normal v2-text-sm v2-text-[#101828] v2-mb-3">
        Please enter your email below. We will email you a link that will allow you to reset your password.
      </p>

      <p className="v2-font-sweet-sans-pro v2-font-normal v2-text-sm v2-text-[#101828] v2-mb-8">
        Note: it may take a few minutes for you to receive this email.
      </p>

      <div className="v2-flex v2-flex-col v2-gap-4 v2-mb-6">
        <div>
          <label
            htmlFor=""
            className="v2-block v2-mb-[6px] v2-text-[#344054] v2-text-sm v2-font-sweet-sans-pro-medium v2-font-medium"
          >
            Email<span>*</span>
          </label>
          <TextInput
            type="email"
            name="email"
            placeholder="Email"
            inputClassName={`v2-font-sweet-sans-pro !v2-py-[7px] placeholder:!v2-text-[#667085] ${
              errors.email ? '!v2-border-[#FDA29B] focus:!v2-border-[#FDA29B] focus:!v2-shadow-none' : ''
            }`}
            value={data.email}
            onChange={handleChange}
            rightIconComponent={
              errors.email ? (
                <Image src={AlertCircle} alt="Error" className="v2-absolute v2-right-3 v2-top-[11px] v2-w-5 v2-h-5" />
              ) : undefined
            }
          />
          {errors.email && (
            <p className="v2-font-sweet-sans-pro v2-text-sm v2-font-light v2-text-[#D92D20] v2-mt-[6px]">
              {errors.email}
            </p>
          )}
          {error && (
            <>
              {error === 'This email is registered with pawtree.com.' ? (
                <p className="v2-font-sweet-sans-pro v2-text-sm v2-font-light v2-mt-[6px]">
                  This email is registered with{' '}
                  <a className="v2-text-[#123E2E] v2-underline" href={RSLOGINURL}>
                    pawtree.com.
                  </a>
                </p>
              ) : (
                <p className="v2-font-sweet-sans-pro v2-text-sm v2-font-light v2-text-[#D92D20] v2-mt-[6px]">{error}</p>
              )}
            </>
          )}
        </div>
      </div>
      <Button
        type="button"
        scheme="primary"
        variant="filled"
        btnSize="lg"
        className="v2-w-full v2-h-[44px] v2-flex v2-justify-center v2-items-center v2-font-sweet-sans-pro v2-text-base !v2-font-bold"
        onClick={handleSubmit}
        disabled={isLoading}
      >
        {isLoading && <Spinner height={20} className="v2-mr-2" />}
        Reset Password
      </Button>
    </div>
  );
};

export default ForgotPasswordForm;
